import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Using YouTube as a data source in Gatsbyjs",
  "description": "",
  "published": "2018-04-24T00:00:00.000Z",
  "redirect_from": ["/blog/youtube-data-source-gatsbyjs/swizec/8347"],
  "categories": "Front End, Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "o_sXQ4fWHac",
      "videostartat": "0"
    }}></lite-youtube>
    <p><a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/"
      }}>{`Gatsby`}</a>{` is hands down the best static site generator out there. Builds sites so fast it looks like a bug.`}</p>
    <p>{`The magic trick Gatsby uses to make sites so fast is binding data build time. Most modern server-side-rendered (ssr) sites build an HTML page of your initial view, load data into the browser, and build the rest of the page live. Or they render a skeleton and fill it with dynamic data.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Just had a CS-y insight into `}<a parentName="p" {...{
          "href": "https://twitter.com/GatsbyJS"
        }}>{`@gatsbyjs`}</a>{` `}<br parentName="p"></br><br parentName="p"></br>{`Gatsby is early binding for code *and* data`}<br parentName="p"></br><br parentName="p"></br>{`Most web apps early bind code (at build) and late bind data. Gatsby does both at build time which really speeds up running the site.`}<br parentName="p"></br><br parentName="p"></br>{`Gatsby can also do late binding for data from the client.`}</p>{`— Kyle Mathews (@kylemathews) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/kylemathews/status/988484209697210368"
      }}>{`April 23, 2018`}</a></blockquote>
    <p>{`Gatsby does all that at build time. Run `}<inlineCode parentName="p">{`gatsby build`}</inlineCode>{`, and it loads your dynamic data and builds static HTML pages with data-dependent parts already baked in.`}</p>
    <h2 {...{
      "id": "how-you-use-it"
    }}>{`How you use it`}</h2>
    <p>{`At a high level, Gatsby uses various source plugins to read data and make it available via a GraphQL interface. You write GraphQL queries to load this data and render React components.`}</p>
    <p>{`Gatsby handles the rest. I'm not really sure how, but smart people assure me it works, and I've never seen it not work. 🤷‍♀️`}</p>
    <p>{`Let's say you want to build pages with markdown files. I do that for my workshop materials. I use a bunch of markdown files published as a website, and it works great.`}</p>
    <p>{`You have to enable the `}<inlineCode parentName="p">{`gatsby-source-filesystem`}</inlineCode>{` plugin. It lets you read local files as a data source. Then you enable the `}<inlineCode parentName="p">{`gatsby-transformer-remark`}</inlineCode>{` plugin, which lets you parse markdown files.`}</p>
    <p>{`When that's set up, you can get a list of page titles with a query like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// anywhere in your JS
export const query = graphql\`
    allMarkdownRemark {
        edges {
            node {
                frontmatter {
                    title
                }
                fields {
                    slug
                }
            }
        }
    }
\`;
`}</code></pre>
    <p>{`You're looking for all nodes of the `}<inlineCode parentName="p">{`markdownRemark`}</inlineCode>{` type, collecting their `}<inlineCode parentName="p">{`edges`}</inlineCode>{` and `}<inlineCode parentName="p">{`nodes`}</inlineCode>{`, and plucking the `}<inlineCode parentName="p">{`title`}</inlineCode>{` and `}<inlineCode parentName="p">{`slug`}</inlineCode>{` value of each.`}</p>
    <p>{`This shows up in your page component as a `}<inlineCode parentName="p">{`data`}</inlineCode>{` prop. So to list all those page titles, you'd do something like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const allPages = _.sortBy(
  data.allMarkdownRemark.edges,
  ({ node }) => node.fields.slug
);

<ul>
  {allPages.map(({ node: { frontmatter: { title } } }) => (
    <li>{title}</li>
  ))}
</ul>;
`}</code></pre>
    <p>{`There's a lot of looping and destructuring because our data comes in the shape of a graph, and I'm bad at writing GraphQL queries. I'm sure this looks terrible to someone who knows what they're doing.`}</p>
    <h2 {...{
      "id": "adding-a-custom-data-source--youtube"
    }}>{`Adding a custom data source – YouTube`}</h2>
    <p>{`The fun part is building your own data sources, which you can do, and it's easier than I thought.`}</p>
    <p>{`Your basic approach goes like this:`}</p>
    <ol>
      <li parentName="ol">{`Search far and wide for an existing plugin`}</li>
      <li parentName="ol">{`Give up and realize you'll have to build it yourself`}</li>
      <li parentName="ol">{`Strongly consider building a releasable plugin`}</li>
      <li parentName="ol">{`Decide you're better off building a one-off integration`}</li>
      <li parentName="ol">{`Spend the next hour fiddling inside `}<inlineCode parentName="li">{`gatsby-node.js`}</inlineCode></li>
    </ol>
    <p>{`Your goal is to read your data source and create Gatsby data nodes for each object you want to make available.`}</p>
    <p>{`The general template goes like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// gatsby-node.js
exports.sourceNodes = async ({ boundActionCreators }) => {
  const { createNode } = boundActionCreators;

  // get data
  // call createNode for each entry
};
`}</code></pre>
    <p>{`You can see `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/learnwhileyoupoop.com/blob/2749ed3654e8f9a222fa6df77cb20e7ead91c174/gatsby-node.js"
      }}>{`my full code on GitHub`}</a>{`.`}</p>
    <p>{`My goal was a list of videos from a YouTube playlist. I used `}<a parentName="p" {...{
        "href": "https://github.com/benkaiser/youtube-playlist-info"
      }}>{`youtube-playlist-info`}</a>{` to fetch a list of videos from a public playlist. Google's official node.js library is too hard to use.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// gatsby-node.js

// require library
const ypi = require("youtube-playlist-info");
// read my API key
const YT_KEY = require("./client_secrets.json")["yt_key"];
// hardcode ID of my playlist for now
const LWyP = "PLF8WgaD4xmjWuh7FTYTealxehOuNor_2S";

exports.sourceNodes = async ({ boundActionCreators }) => {
  const { createNode } = boundActionCreators;
  const items = await ypi(YT_KEY, LWyP);

  // build Gatsby nodes
};
`}</code></pre>
    <p>{`When Gatsby builds my site, it goes into `}<inlineCode parentName="p">{`gatsby-nodes`}</inlineCode>{` and calls my node builder function. This uses `}<inlineCode parentName="p">{`ypi`}</inlineCode>{` to fetch a list of videos from the hardcoded `}<a parentName="p" {...{
        "href": "https://www.youtube.com/playlist?list=PLF8WgaD4xmjWuh7FTYTealxehOuNor_2S"
      }}>{`Learn While You Poop playlist`}</a>{`.`}</p>
    <p>{`Yes, that means building the site is now slow. It has to talk to YouTube's API every time, but it’s worth it.`}</p>
    <p>{`Your next step is to build Gatsby nodes for each video in the playlist.`}</p>
    <p>{`Nodes require a `}<inlineCode parentName="p">{`contentDigest`}</inlineCode>{` so Gatsby can tell whether they've changed. To make that easier, I built a helper function 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// gatsby-node.js

const makeNode = (node) => {
  node.internal.contentDigest = crypto
    .createHash("md5")
    .update(JSON.stringify(node))
    .digest("hex");

  createNode(node);
};
`}</code></pre>
    <p><inlineCode parentName="p">{`makeNode`}</inlineCode>{` is a thin wrapper on Gatsby's built-in `}<inlineCode parentName="p">{`createNode`}</inlineCode>{` function that automatically generates an `}<inlineCode parentName="p">{`md5`}</inlineCode>{` hash of the whole node and saves it in `}<inlineCode parentName="p">{`contentDigest`}</inlineCode>{`. Not sure why this isn't default behavior.`}</p>
    <p>{`With that set up, you can build your nodes in a loop like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// gatsby-node

let lwypNode = {
  id: "lwypPlaylist",
  parent: "ytPlaylists",
  children: [],
  internal: {
    type: "ytPlaylist",
  },
};

lwypNode.children = items.map(
  ({ title, description, resourceId, thumbnails, position }) => {
    const id = \`ytVideo-\${resourceId.videoId}\`;
    makeNode({
      id,
      title,
      description,
      thumbnails,
      position,
      resourceId,
      internal: {
        type: "ytVideo",
      },
      parent: "lwypPlaylist",
      children: [],
    });
    return id;
  }
);

makeNode(lwypNode);
`}</code></pre>
    <p>{`Set up an empty node for the `}<inlineCode parentName="p">{`lwypPlaylist`}</inlineCode>{` of type `}<inlineCode parentName="p">{`ytPlaylist`}</inlineCode>{`. Fill its children with nodes that contain important data from the YouTube API.`}</p>
    <p>{`When all those nodes are created, `}<em parentName="p">{`then`}</em>{` create the main `}<inlineCode parentName="p">{`lwypNode`}</inlineCode>{`. This reverse order is important. You have to build your data graph from the bottom up otherwise you'll get strange errors.`}</p>
    <p>{`You can see me struggling with that in the video above. Lots of head scratching.`}</p>
    <h2 {...{
      "id": "read-your-youtube-data-source"
    }}>{`Read your YouTube data source`}</h2>
    <p>{`When your nodes are created, you can read them anywhere inside Gatsby with a GraphQL query like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export const query = graphql\`
  query LwypPlaylist {
    ytPlaylist(id: { eq: "lwypPlaylist" }) {
      childrenYtVideo {
        id
        title
        description
      }
    }
  }
\`;
`}</code></pre>
    <p>{`Fetch a node of type `}<inlineCode parentName="p">{`ytPlaylist`}</inlineCode>{` with `}<inlineCode parentName="p">{`id`}</inlineCode>{` equal to `}<inlineCode parentName="p">{`lwypPlaylist`}</inlineCode>{`. Inside that node, get all children `}<inlineCode parentName="p">{`ytVideo`}</inlineCode>{` nodes and their `}<inlineCode parentName="p">{`id`}</inlineCode>{`, `}<inlineCode parentName="p">{`title`}</inlineCode>{`, and `}<inlineCode parentName="p">{`description`}</inlineCode>{`.`}</p>
    <p>{`You can then render them in a loop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Component = ({ data }) => {
  const videos = data.ytPlaylist.childrenYtVideo;

  return (
    <div>
      {videos.map((video) => (
        <div>
          <h3>{video.title}</h3>
          {video.description}
        </div>
      ))}
    </div>
  );
};
`}</code></pre>
    <p>{`Full page coming soon.`}</p>
    <p><img parentName="p" {...{
        "src": "/eb154423bbc0f9ecd51269c9bc49b559/media-u0v3z2iQLxyHC-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Should I make a real YouTube source plugin for Gatsby? `}<a parentName="p" {...{
        "href": "https://twitter.com/Swizec"
      }}>{`Ping me on Twitter`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      